import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { Facts as FactsOf, PendingMessage } from '../api-types';
import { formatWhen, paragraphsOf, recipientName } from '../format';
import { useLabels } from '../labels';

/** The rendered text as plain paragraphs: the API's text is never parsed as HTML. */
export function PlainText({ text }: { readonly text: string }) {
  return (
    <div className="flex flex-col gap-2 text-sm leading-relaxed">
      {paragraphsOf(text).map((paragraph, index) => (
        <p key={index} className="whitespace-pre-line">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

function Facts({ facts }: { readonly facts: FactsOf }) {
  const { t } = useTranslation('communications');
  const labels = useLabels();
  const entries = Object.entries(facts);
  return (
    <div>
      <p className="field-label">{t('approvals.facts')}</p>
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('approvals.noFacts')}</p>
      ) : (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {entries.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-muted-foreground">{labels.fact(key)}</dt>
              <dd className="break-words">
                {typeof value === 'boolean'
                  ? t(value ? 'approvals.yes' : 'approvals.no')
                  : String(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export type CardActions = {
  readonly busy: boolean;
  readonly onApprove: () => void;
  readonly onEdit: () => void;
  readonly onPostpone: () => void;
  readonly onDiscard: () => void;
};

/** One email waiting for the Director: who, which step, the text, the facts, the four decisions. */
export function ApprovalCard({
  item,
  actions,
}: {
  readonly item: PendingMessage;
  readonly actions: CardActions;
}) {
  const { t, i18n } = useTranslation('communications');
  const labels = useLabels();
  const { message } = item;
  const name = recipientName(item);
  const tap = 'min-h-11 @min-[620px]/shell:min-h-[38px]';
  return (
    <li className="border-border bg-card flex flex-col gap-4 rounded-xl border p-4">
      <header className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{t('approvals.to', { name })}</h3>
        <p className="text-muted-foreground text-sm">
          {t('approvals.profile')}: {labels.profile(message.profile ?? null)} ·{' '}
          {t('approvals.step')}: {labels.step(message.step)} ·{' '}
          {t('approvals.created', { when: formatWhen(message.createdAt, i18n.resolvedLanguage) })}
        </p>
      </header>
      <div className="border-border flex flex-col gap-2 rounded-lg border p-3">
        <p className="text-sm font-semibold">{message.subject}</p>
        <PlainText text={message.bodyText} />
      </div>
      <Facts facts={message.facts} />
      <div className="flex flex-wrap gap-2">
        <Button type="button" className={tap} disabled={actions.busy} onClick={actions.onApprove}>
          {t('approvals.approve')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={tap}
          disabled={actions.busy}
          onClick={actions.onEdit}
        >
          {t('approvals.editApprove')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={tap}
          disabled={actions.busy}
          onClick={actions.onPostpone}
        >
          {t('approvals.postpone')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={`${tap} text-destructive`}
          disabled={actions.busy}
          onClick={actions.onDiscard}
        >
          {t('approvals.discard')}
        </Button>
      </div>
    </li>
  );
}
